import React, { useState } from 'react';
import { SCORECARD_LAYOUTS, ScorecardLayout } from './types';
import { SCORECARD_LAYOUT_COMPONENTS } from './layouts';

const SAMPLE_SCORE = '=' as const;
const noop = () => {};

/**
 * Library preview — shows one layout at a time and lets the user switch
 * between the available variants via a small "L1 / L2 / …" pill bar.
 *
 * Adding a new variant to layouts/index.ts is enough; this preview will
 * pick it up automatically.
 */
const Preview: React.FC = () => {
  const [active, setActive] = useState<ScorecardLayout>(
    SCORECARD_LAYOUTS[0]?.value ?? 'carousel',
  );

  const ActiveLayout = SCORECARD_LAYOUT_COMPONENTS[active];
  const activeMeta = SCORECARD_LAYOUTS.find(v => v.value === active);

  return (
    <div className="h-full bg-surface flex flex-col">
      {/* Layout switcher */}
      <div className="px-4 pt-3 pb-2 shrink-0 flex items-center justify-between gap-3">
        <span className="text-[10px] text-neutral-400 uppercase tracking-wider font-medium truncate">
          {activeMeta?.label}
        </span>
        <div
          role="radiogroup"
          aria-label="Layout variant"
          className="flex items-center gap-1 p-0.5 rounded-md border border-neutral-700 bg-neutral-900/40"
        >
          {SCORECARD_LAYOUTS.map((variant, idx) => {
            const isActive = active === variant.value;
            return (
              <button
                key={variant.value}
                type="button"
                role="radio"
                aria-checked={isActive}
                title={variant.label}
                onClick={() => setActive(variant.value)}
                className={`px-2 h-6 rounded text-[10px] font-semibold tracking-wide transition-colors ${
                  isActive
                    ? 'bg-neutral-700 text-neutral-100'
                    : 'text-neutral-400 hover:text-neutral-200'
                }`}
              >
                L{idx + 1}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active layout */}
      <div className="flex-1 min-h-0">
        <ActiveLayout currentScore={SAMPLE_SCORE} onSelect={noop} readOnly />
      </div>
    </div>
  );
};

export default Preview;
